import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, RefreshCw, QrCode, Copy, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PageHeader, { ghostBtn } from "@/app/PageHeader";
import { PUBLIC_SITE, useSession } from "@/app/session";

const selectCls = "h-10 w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";

// Customer sites, each tied to an account in Accounts & Service and with a branded QR card that opens
// the public feedback page for that location. ?account=<id> pre-picks the account for a new location;
// ?qr=<id> opens that location's QR card.
const Locations = () => {
  const { api, onAuthError } = useSession();
  const [params, setParams] = useSearchParams();
  const [locations, setLocations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", address: "", account_id: params.get("account") || "" });
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [qrLoc, setQrLoc] = useState(null);
  const handleAuthError = onAuthError;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/locations")
      .then((res) => setLocations(res.data))
      .catch((err) => handleAuthError(err, "Could not load locations."))
      .finally(() => setLoading(false));
  }, [api, handleAuthError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/service/accounts").then((res) => setAccounts(res.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    const id = params.get("qr");
    const loc = id && locations.find((l) => l.id === id);
    if (loc) {
      setQrLoc(loc);
      setParams({}, { replace: true });
    }
  }, [params, locations, setParams]);

  const pickAccount = (accountId) => {
    const acc = accounts.find((a) => a.id === accountId);
    setForm((f) => ({ ...f, account_id: accountId, address: f.address || acc?.address || "" }));
  };

  const assign = async (loc, accountId) => {
    try {
      await api.put(`/locations/${loc.id}`, { name: loc.name, address: loc.address || "", account_id: accountId });
      const acc = accounts.find((a) => a.id === accountId);
      toast.success(acc ? `"${loc.name}" is now under ${acc.name}.` : `"${loc.name}" is no longer tied to an account.`);
      load();
    } catch (err) {
      handleAuthError(err, "Could not change the account.");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/locations", form);
      toast.success(`"${form.name}" added. Its QR code is ready in the list.`);
      setForm({ name: "", address: "", account_id: form.account_id });
      load();
    } catch (err) {
      handleAuthError(err, "Could not add location.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (loc) => {
    setDeletingId(loc.id);
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success(`"${loc.name}" removed.`);
      setLocations((prev) => prev.filter((l) => l.id !== loc.id));
    } catch (err) {
      handleAuthError(err, "Could not delete location.");
    } finally {
      setDeletingId(null);
    }
  };

  const qrUrl = qrLoc ? `${PUBLIC_SITE}/feedback?location=${qrLoc.id}` : "";

  const copyQrUrl = () => {
    navigator.clipboard.writeText(qrUrl)
      .then(() => toast.success("Feedback link copied."))
      .catch(() => toast.error("Could not copy link."));
  };

  const downloadQr = () => {
    const svg = document.querySelector('[data-testid="qr-code-svg"]');
    if (!svg || !qrLoc) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const scale = 4;
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `vortex-qr-${qrLoc.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const downloadCard = () => {
    const svg = document.querySelector('[data-testid="qr-code-svg"]');
    if (!svg || !qrLoc) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const qrImg = new Image();
    const logoImg = new Image();
    const machineImg = new Image();
    let loaded = 0;
    const tryDraw = () => {
      loaded += 1;
      if (loaded < 3) return;
      const W = 1000, H = 1400;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#050814";
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(92, 179, 226, 0.45)";
      ctx.lineWidth = 3;
      ctx.strokeRect(28, 28, W - 56, H - 56);

      const logoW = 400;
      const logoH = logoW * (269 / 962);
      ctx.drawImage(logoImg, (W - logoW) / 2, 80, logoW, logoH);

      const qrSize = 460;
      const qx = (W - qrSize) / 2;
      const qy = 265;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.roundRect(qx - 22, qy - 22, qrSize + 44, qrSize + 44, 28);
      ctx.fill();
      ctx.drawImage(qrImg, qx, qy, qrSize, qrSize);

      ctx.textAlign = "center";
      ctx.fillStyle = "#5CB3E2";
      ctx.font = "700 44px Outfit, sans-serif";
      ctx.fillText("SCAN TO TELL US ANYTHING", W / 2, qy + qrSize + 105);

      ctx.fillStyle = "#F8FAFC";
      ctx.font = "500 32px 'DM Sans', sans-serif";
      const words = qrLoc.name.split(" ");
      const lines = [];
      let line = "";
      words.forEach((w) => {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > W - 220 && line) { lines.push(line); line = w; } else { line = test; }
      });
      if (line) lines.push(line);
      lines.forEach((l, i) => ctx.fillText(l, W / 2, qy + qrSize + 170 + i * 42));
      const afterLoc = qy + qrSize + 170 + lines.length * 42;

      const bandH = 165;
      const bandY = Math.max(afterLoc + 25, H - 330);
      const bandX = 70, bandW = W - 140;
      const srcRatio = machineImg.width / machineImg.height;
      const targetRatio = bandW / bandH;
      let sw = machineImg.width, sh = machineImg.height, sx = 0, sy = 0;
      if (srcRatio > targetRatio) { sw = sh * targetRatio; sx = (machineImg.width - sw) / 2; }
      else { sh = sw / targetRatio; sy = (machineImg.height - sh) / 2; }
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(bandX, bandY, bandW, bandH, 20);
      ctx.clip();
      ctx.drawImage(machineImg, sx, sy, sw, sh, bandX, bandY, bandW, bandH);
      ctx.restore();

      ctx.fillStyle = "#94A3B8";
      ctx.font = "400 20px 'DM Sans', sans-serif";
      ctx.fillText("Modern Vending for a Modern World", W / 2, H - 62);

      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `vortex-qr-card-${qrLoc.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      a.click();
    };
    qrImg.onload = tryDraw;
    logoImg.onload = tryDraw;
    machineImg.onload = tryDraw;
    qrImg.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    logoImg.src = "/brand/logo-light.png";
    machineImg.src = "/images/smart-cooler-hero.800w.d28942cc.webp";
  };

  return (
    <div data-testid="admin-page">
        <PageHeader title="Locations & QR" testId="admin-heading"
          intro="Each location belongs to a customer account. Download its branded QR card for the machine; scanning it opens the feedback page with that location filled in, and the feedback shows up on the account."
          actions={<button data-testid="admin-refresh-button" onClick={load} className={ghostBtn}><RefreshCw size={14} /> Refresh</button>} />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <form onSubmit={add} className="border border-white/10 bg-vortex-surface/60 p-8 lg:col-span-4" data-testid="admin-add-form">
            <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Add Location</p>
            <div className="mt-6 space-y-2">
              <Label htmlFor="loc-account" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Account</Label>
              <select id="loc-account" data-testid="admin-location-account-select" value={form.account_id}
                onChange={(e) => pickAccount(e.target.value)} className={selectCls}>
                <option value="">No account yet</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="mt-5 space-y-2">
              <Label htmlFor="loc-name" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Name</Label>
              <Input id="loc-name" data-testid="admin-location-name-input" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan"
                placeholder="e.g. Harbor Tech Park — Floor 3" />
            </div>
            <div className="mt-5 space-y-2">
              <Label htmlFor="loc-address" className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Address (optional)</Label>
              <Input id="loc-address" data-testid="admin-location-address-input" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan"
                placeholder="Street, city, notes" />
            </div>
            <button
              type="submit"
              disabled={adding}
              data-testid="admin-add-location-button"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50"
            >
              <Plus size={15} /> {adding ? "Adding..." : "Add location"}
            </button>
          </form>

          <div className="lg:col-span-8">
            <div className="border border-white/10" data-testid="admin-locations-table">
              <div className="grid grid-cols-12 gap-4 border-b border-white/10 bg-vortex-surface/60 px-6 py-4 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
                <span className="col-span-6 sm:col-span-5">Location</span>
                <span className="col-span-4 sm:col-span-5">Account</span>
                <span className="col-span-2 text-right">Action</span>
              </div>
              {loading ? (
                <p className="px-6 py-10 text-sm text-slate-500" data-testid="admin-loading">Loading locations...</p>
              ) : locations.length === 0 ? (
                <p className="px-6 py-10 text-sm text-slate-500" data-testid="admin-empty-state">
                  No locations yet. Add your first customer site on the left.
                </p>
              ) : (
                locations.map((loc) => (
                  <div
                    key={loc.id}
                    className="group grid grid-cols-12 items-center gap-4 border-b border-white/5 px-6 py-5 transition-colors duration-300 hover:bg-vortex-surface/40"
                    data-testid={`admin-location-row-${loc.id}`}
                  >
                    <div className="col-span-6 flex min-w-0 items-start gap-3 sm:col-span-5">
                      <MapPin size={15} className="mt-0.5 shrink-0 text-vortex-cyan" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-100">{loc.name}</p>
                        {loc.address && <p className="truncate text-xs text-slate-500">{loc.address}</p>}
                      </div>
                    </div>
                    <div className="col-span-4 min-w-0 space-y-1 sm:col-span-5">
                      <select aria-label={`Account for ${loc.name}`} value={loc.account_id || ""}
                        onChange={(e) => assign(loc, e.target.value)} className={selectCls}
                        data-testid={`admin-location-account-${loc.id}`}>
                        <option value="">No account</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                      {loc.account_id && (
                        <Link to={`/admin/service?account=${loc.account_id}`}
                          className="block truncate font-mono text-[10px] tracking-[0.15em] uppercase text-vortex-cyan hover:underline">
                          Open account
                        </Link>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-3">
                      <button
                        data-testid={`admin-qr-button-${loc.id}`}
                        onClick={() => setQrLoc(loc)}
                        className="text-slate-500 transition-colors hover:text-vortex-cyan"
                        aria-label={`QR code for ${loc.name}`}
                      >
                        <QrCode size={16} />
                      </button>
                      <button
                        data-testid={`admin-delete-button-${loc.id}`}
                        onClick={() => remove(loc)}
                        disabled={deletingId === loc.id}
                        className="text-slate-500 transition-colors hover:text-red-400 disabled:opacity-40"
                        aria-label={`Delete ${loc.name}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-slate-600 uppercase" data-testid="admin-count">
              {locations.length} location{locations.length === 1 ? "" : "s"} live on the feedback page
            </p>
          </div>
        </div>

        <Dialog open={!!qrLoc} onOpenChange={(open) => !open && setQrLoc(null)}>
          <DialogContent className="border-white/10 bg-vortex-surface text-slate-100 sm:max-w-md" data-testid="qr-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold tracking-tight text-white">
                Machine QR Code
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-400">
                Print this and stick it on the cooler at{" "}
                <span className="text-vortex-cyan">{qrLoc?.name}</span>. Scanning it opens the
                feedback page with this location already selected.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="rounded-xl bg-white p-5 glow-cyan">
                {qrLoc && (
                  <QRCodeSVG
                    value={qrUrl}
                    size={200}
                    level="M"
                    fgColor="#050814"
                    bgColor="#ffffff"
                    data-testid="qr-code-svg"
                  />
                )}
              </div>
              <div className="flex w-full items-center gap-2 rounded-lg border border-white/10 bg-vortex-ink/60 px-4 py-3">
                <span className="flex-1 truncate font-mono text-xs text-slate-400" data-testid="qr-url-text">{qrUrl}</span>
                <button
                  data-testid="qr-copy-button"
                  onClick={copyQrUrl}
                  className="shrink-0 text-slate-400 transition-colors hover:text-vortex-cyan"
                  aria-label="Copy feedback link"
                >
                  <Copy size={15} />
                </button>
              </div>
              <div className="flex w-full flex-col items-center gap-3">
                <button
                  data-testid="qr-download-card-button"
                  onClick={downloadCard}
                  className="flex items-center gap-2 rounded-full bg-vortex-cyan px-8 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)]"
                >
                  <Download size={15} /> Download print card
                </button>
                <button
                  data-testid="qr-download-button"
                  onClick={downloadQr}
                  className="flex items-center gap-2 rounded-full border border-white/15 px-8 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
                >
                  <Download size={15} /> Plain QR PNG
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default Locations;
